import { SagaIterator } from 'redux-saga';
import { call, cancel, delay, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  CHANGE_SEARCH_DIALOG_STATE,
  changeSearchDialogState,
  getBezeichnungForPerson,
  SearchDialogContainerStateChangeAction,
} from '@ps20-frontend/gui-components';
import {
  AngebotsId,
  DoRequestResponse,
  getEndpointUrl,
  GlobalActions,
  GlobalPut,
  ModalCloseReason,
  ModalType,
  ProvideVermittlernummerAction,
} from '@ps20-frontend/shop-integration-redux';
import {
  BankverbindungVorvertrag,
  BasicVertragsdaten,
  PersonensucheParty,
  PersonensuchePersonenart,
  PersonensucheRequest,
  PersonensucheResponse,
  SearchFilter,
  VertragsuebersichtRequestModel,
  VertragsuebersichtResponseModel,
  WeitereVertraegeVertragsdaten,
} from '@ps20/bff-models';
import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import {
  App,
  ConfirmationState,
  FremdVN,
  getSpartenTypeByApp,
  mapAmisCrmResponseToPersonenSearchList,
  mapFremdVNVorvertraege,
  markAsDisabled,
  PersonenSearchListItem,
  PersonensucheModal,
  SearchlistItem,
  StandaloneProdukt,
  Vorvertrag,
} from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { isOlderThan } from '@ps20-frontend/utils';
import dayjs from 'dayjs';
import { AnyAction } from 'redux';
import { DashboardActions } from '../dashboard.actions';
import { hasPersonVorvertraege, selectInvalidWarenkorbProdukte, } from '../selectors/vorvertragsUebersicht.selector';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { angebotIsTarifiert } from '../selectors/warenkorb.selector';
import {
  selectPersonensucheModal,
  selectUnfallPersonAuswahlModalOpenForAngebot,
  shouldUseSdwEndpoint,
} from '../selectors/transient-state.selector';
import {
  mapVertragItemToVorvertrag,
  VertragItem,
  VertragItemWithFullContract,
} from '../reducers/weitereVertraege.reducer';
import { selectIsVn } from '../selectors/versicherungsnehmer.selector';
import {
  extractStandaloneVertraegeFromResponse,
  extractStandardVertraegeFromResponse,
  mapHinweisMeldungen,
  VORVERTRAGSUEBERSICHT_EVENTS,
} from './vorvertragsUebersichtSaga';
import {
  buildInvalidWarenkorbErrorText,
  getInvalideWarenkorbAngebote,
} from './helper-functions/invalidWarenkorbErrorHelper';


const url = '/privatschutz/api/personensuche';

export enum FREMD_VERTRAEGE_PERSONEN_SUCHE_EVENTS {
  SUCHEN_SUCCESS = 'FREMD_VERTRAEGE_PERSONEN_SUCHE_SUCCESS',
  SUCHEN_FAILED = 'FREMD_VERTRAEGE_PERSONEN_SUCHE_FAILED',
}

export enum VORVERTRAGREQUEST_EVENTS {
  REQUEST_FREMDVERTRAG_SUCCESS = 'REQUEST_FREMDVERTRAG_SUCCESS',
  REQUEST_FREMDVERTRAG_FAILED = 'REQUEST_FREMDVERTRAG_FAILED',
}

export function* watchPersonenSucheAndUpdateVertraegeSaga(): SagaIterator {
  yield takeEvery(CHANGE_SEARCH_DIALOG_STATE, safeSaga(requestPersonenSuche, App.SHOP_DASHBOARD));
  yield takeEvery(CHANGE_SEARCH_DIALOG_STATE, safeSaga(validateSelectedItem, App.SHOP_DASHBOARD));
  yield takeEvery(CHANGE_SEARCH_DIALOG_STATE, safeSaga(confirmPerson, App.SHOP_DASHBOARD));
  yield takeEvery(
    CHANGE_SEARCH_DIALOG_STATE,
    safeSaga(navigateToUnfallEingabe, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    FREMD_VERTRAEGE_PERSONEN_SUCHE_EVENTS.SUCHEN_SUCCESS,
    safeSaga(successCallback, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    FREMD_VERTRAEGE_PERSONEN_SUCHE_EVENTS.SUCHEN_FAILED,
    safeSaga(errorCallback, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    VORVERTRAGREQUEST_EVENTS.REQUEST_FREMDVERTRAG_SUCCESS,
    safeSaga(handleVorvertraegeResponse, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    VORVERTRAGREQUEST_EVENTS.REQUEST_FREMDVERTRAG_FAILED,
    safeSaga(handleRequestError, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    DashboardActions.START_FREMD_VERTRAEGE_AENDERN,
    safeSaga(getVertragsdatenDetails, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    DashboardActions.WEITERE_VERTRAEGE_UBERNEHMEN_CACHE,
    safeSaga(getVertragsdatenDetails, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    VORVERTRAGSUEBERSICHT_EVENTS.REQUEST_VORVERTRAGSUEBERSICHT_FAILED,
    safeSaga(handleRequestError, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    VORVERTRAGSUEBERSICHT_EVENTS.REQUEST_VORVERTRAGSUEBERSICHT_SUCCESS,
    safeSaga(handleVertragdatenDetailsResponse, App.SHOP_DASHBOARD),
  );
}

export function* requestPersonenSuche(
  action: SearchDialogContainerStateChangeAction<SearchlistItem>,
): SagaIterator {
  if (
    action.payload.searchString === undefined &&
    action.payload.exactNameSearchString === undefined
  ) {
    return yield cancel();
  }

  yield put(
    changeSearchDialogState({
      loading: true,
      error: false,
      items: [],
      selectedItem: null,
    }),
  );

  const state: IAppState = yield select();
  const request: PersonensucheRequest = {
    firstName: action.payload.exactNameSearchString?.firstName,
    lastName: action.payload.exactNameSearchString?.lastName,
    searchPattern: action.payload?.searchString,
    searchFilters:
      selectPersonensucheModal(state) === PersonensucheModal.UNFALL
        ? [SearchFilter.NAT_PERS_ONLY]
        : [SearchFilter.KUNDENNUMMER_NOT_NULL],
  };

  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().doRequest({
      appId: App.SHOP_DASHBOARD,
      body: request,
      url: getEndpointUrl(shouldUseSdwEndpoint(state), url),
      successEvent: FREMD_VERTRAEGE_PERSONEN_SUCHE_EVENTS.SUCHEN_SUCCESS,
      errorEvent: FREMD_VERTRAEGE_PERSONEN_SUCHE_EVENTS.SUCHEN_FAILED,
      noCache: true,
    }),
  );
}

export function* validateSelectedItem(
  action: SearchDialogContainerStateChangeAction<PersonenSearchListItem>,
): SagaIterator {
  if (action.payload.selectedItem == null) {
    return yield cancel();
  }

  yield put(changeSearchDialogState({ confirmation: ConfirmationState.SUCCESS }));
}

export function* confirmPerson(
  action: SearchDialogContainerStateChangeAction<PersonenSearchListItem>,
): SagaIterator {
  if (action.payload.confirmed !== true) {
    return yield cancel();
  }
  yield put(changeSearchDialogState({ confirmation: ConfirmationState.IN_PROGRESS }));

  const state: IAppState = yield select();
  if (selectPersonensucheModal(state) === PersonensucheModal.FREMDVERTRAG) {
    const requestVorvertraegePayload = yield call(generateRequestVorvertraegePayload);
    yield call(requestVorvertraege, requestVorvertraegePayload);
  }
}

export function* navigateToUnfallEingabe(
  action: SearchDialogContainerStateChangeAction<PersonenSearchListItem>,
): SagaIterator {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();

  if (
    selectPersonensucheModal(state) !== PersonensucheModal.UNFALL ||
    action.payload.confirmed !== true
  ) {
    return yield cancel();
  }

  const selectedItemPayload = state.transientState.kundenSuchdialog.selectedItem.personData;

  const person: PersonensucheParty = {
    ...(selectedItemPayload as PersonensucheParty),
    personenart: PersonensuchePersonenart.NATUERLICHE_PERSON,
    datenVollstaendig: false,
  };
  const unfAngebotsId = selectUnfallPersonAuswahlModalOpenForAngebot(state);
  yield call(globalPut.put, new GlobalActions().closePersonensucheModal());
  yield call(
    globalPut.put,
    new GlobalActions().unfallVpUebernehmen(
      person,
      selectIsVn(state, person.kundenverwaltungID),
      unfAngebotsId,
    ),
  );
  yield call(
    globalPut.put,
    new GlobalActions().navigationRequested({
      appId: App.UNF,
      subroute: '/eingabe',
      angebotsId: unfAngebotsId,
    }),
  );
}

function* generateRequestVorvertraegePayload(): SagaIterator {
  const state: IAppState = yield select();
  const selectedItemPayload = state.transientState.kundenSuchdialog.selectedItem.personData;
  const kundennummer = selectedItemPayload.kundennummer;
  yield call(GlobalPut.getInstance().put, new GlobalActions().requestVermittlernummer());
  const { payload: vermittlernummer }: ProvideVermittlernummerAction = yield take(
    GlobalActions.PROVIDE_VERMITTLERNUMMER,
  );
  const requestVorvertraegePayload: VertragsuebersichtRequestModel = vermittlernummer
    ? {
      kundennummer,
      vertretungsnummer: vermittlernummer,
      gesellschForm: state.versicherungsnehmer.businessForm?.wert,
    }
    : {
      kundennummer,
      gesellschForm: state.versicherungsnehmer.businessForm?.wert,
    };
  return requestVorvertraegePayload;
}

export function* successCallback(response: { payload: PersonensucheResponse }): SagaIterator {
  const appState: IAppState = yield select();

  const personenSearchListItems: PersonenSearchListItem[] = response.payload.personen
    .map(person => mapAmisCrmResponseToPersonenSearchList(person))
    .filter(kunde => kunde.personData.kundennummer !== appState.kundennummer);

  if (isUnfall(appState)) {
    personenSearchListItems
      .filter(
        person =>
          isOlderThan99(person) ||
          isPersonBereitsTarifiert(person, appState) ||
          getHasPersonVorvertraege(person, appState),
      )
      .forEach(markAsDisabled);
  }

  yield put(
    changeSearchDialogState({
      loading: false,
      error: false,
      items: personenSearchListItems,
    }),
  );
}

export function* errorCallback(response: { payload: { response: { data: { message: string } } } }): SagaIterator {
  yield put(
    changeSearchDialogState({
      loading: false,
      error: true,
      errorMessage: response.payload.response.data.message,
    }),
  );
}

export function* requestVorvertraege(requestModel: VertragsuebersichtRequestModel): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put,
    new GlobalActions().doRequest({
      appId: App.SHOP_DASHBOARD,
      url: '/privatschutz/api/fremdvertragsuebersicht',
      body: requestModel,
      successEvent: VORVERTRAGREQUEST_EVENTS.REQUEST_FREMDVERTRAG_SUCCESS,
      errorEvent: VORVERTRAGREQUEST_EVENTS.REQUEST_FREMDVERTRAG_FAILED,
    }),
  );
}

export function* handleVorvertraegeResponse(
  successAction: DoRequestResponse<VertragsuebersichtResponseModel>,
): SagaIterator {
  const state: IAppState = yield select();
  if (selectPersonensucheModal(state) !== PersonensucheModal.FREMDVERTRAG) {
    return yield cancel();
  }
  yield put(
    changeSearchDialogState({
      loading: false,
      error: false,
      open: false,
      confirmation: ConfirmationState.SUCCESS,
      searchString: undefined,
    }),
  );
  const origResponse: VertragsuebersichtResponseModel = successAction.payload;
  const payload: {
    vorvertraege: Array<Vorvertrag>;
    standaloneprodukte: Array<StandaloneProdukt>;
  } = {
    vorvertraege: extractStandardVertraegeFromResponse(origResponse),
    standaloneprodukte: extractStandaloneVertraegeFromResponse(origResponse),
  };

  yield delay(0);
  yield put(new DashboardActions().updateVorvertraegeForModal(payload));
}

export function* handleRequestError(): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  yield put(new DashboardActions().updateFremdvertraegeFinished());
  yield put(new DashboardActions().updateWeitereVertraegeFinished());
  yield call(globalPut.put, new GlobalActions().errorPageWithCustomContentRequested('vorvertrag'));
}

export function* getVertragsdatenDetails(action: AnyAction): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  if (action.type === DashboardActions.WEITERE_VERTRAEGE_UBERNEHMEN_CACHE) {
    yield put(new DashboardActions().updateWeitereVertraegeStarted());
  } else {
    yield put(new DashboardActions().updateFremdvertraegeStarted());
  }

  yield call(globalPut.put, new GlobalActions().checkVorvertraege());
}

export function* handleVertragdatenDetailsResponse(
  successAction: DoRequestResponse<VertragsuebersichtResponseModel>,
): SagaIterator {
  const state: IAppState = yield select();

  if (
    !state.transientState.updatefremdvertraegeIsInProgress &&
    !state.transientState.updateWeitereVertraegeIsInProgress
  ) {
    return;
  }

  const payload: VertragsuebersichtResponseModel = successAction.payload;
  const invalidWarenkorbProdukte = selectInvalidWarenkorbProdukte(
    payload.invalidWarenkorbProdukte,
    state,
  );
  if (invalidWarenkorbProdukte?.length > 0) {
    const alleInvalideAngebote: Array<WarenkorbAngebot> = getInvalideWarenkorbAngebote(
      state.warenkorb,
      invalidWarenkorbProdukte,
    );
    const hinweisText: string = buildInvalidWarenkorbErrorText(alleInvalideAngebote);
    const alleInvalideAngebotsIds: Array<AngebotsId> = alleInvalideAngebote.map(angebot => ({
      angebotsId: angebot.angebotsId,
      appId: angebot.appId,
    }));
    if (alleInvalideAngebote.some(angebot => angebotIsTarifiert(angebot))) {
      yield call(
        GlobalPut.getInstance().put,
        new GlobalActions().requestModal(ModalType.FREMDVN_WARENKORB_HINWEIS, {
          body: hinweisText,
        }),
      );
      const closeModalAction = yield take(GlobalActions.CLOSE_MODAL);
      if (closeModalAction.payload === ModalCloseReason.PRIMARY) {
        yield call(finalizeStateChanges, alleInvalideAngebotsIds, state, payload);
      }
    } else {
      yield call(finalizeStateChanges, alleInvalideAngebotsIds, state, payload);
    }
  } else {
    yield* dispatchActionsIfUpdatefremdvertraegeIsInProgress(state);
    yield* dispatchActionsIfUpdateWeitereVertraegeIsInProgress(state, payload);
    yield call(updateVertragdaten, payload);
  }
  if (state.transientState.updatefremdvertraegeIsInProgress) {
    yield put(new DashboardActions().updateFremdvertraegeFinished());
  }
  if (state.transientState.updateWeitereVertraegeIsInProgress) {
    yield put(new DashboardActions().updateWeitereVertraegeFinished());
  }
}

export function* finalizeStateChanges(
  invalidAngebotsIds: Array<AngebotsId>,
  state: IAppState,
  payload: VertragsuebersichtResponseModel,
): SagaIterator {
  yield call(GlobalPut.getInstance().put, new GlobalActions().produktDeleted(invalidAngebotsIds));
  yield* dispatchActionsIfUpdatefremdvertraegeIsInProgress(state);
  yield* dispatchActionsIfUpdateWeitereVertraegeIsInProgress(state, payload);
  yield call(updateVertragdaten, payload);
}

function* dispatchActionsIfUpdatefremdvertraegeIsInProgress(state: IAppState): SagaIterator {
  if (state.transientState.updatefremdvertraegeIsInProgress) {
    yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().vnWechselChanged(state.transientState.fremdVnChanged),
    );
    yield put(
      new DashboardActions().finishFremdVertraegeAendern(
        state.transientState.newFremdVertraegeState,
      ),
    );
  }
}

function* dispatchActionsIfUpdateWeitereVertraegeIsInProgress(
  state: IAppState,
  responseModel?: VertragsuebersichtResponseModel,
): SagaIterator {
  if (state.transientState.updateWeitereVertraegeIsInProgress) {
    const weitereVertraegeCached = state.transientState.weitereVertraegeCached;
    let enrichedWeitereVertraege: Array<VertragItem | VertragItemWithFullContract> = weitereVertraegeCached;
    let fullContracts: Array<Partial<Vorvertrag>> = [];

    if (responseModel?.weitereStandardProdukte) {
      const weitereStandardProdukte = responseModel.weitereStandardProdukte;
      fullContracts = weitereStandardProdukte.map(item => {
        const contract: Partial<Vorvertrag> = {
          vertragsnummer: item.vertragsnummer,
          ablaufdatum: item.ablaufdatum as string | undefined,
          isStorniert: item.storniert,
          stornoDetail: item.stornoDetail,
          spLaufnummer: item.spLaufnummer,
          eigenschaftHinweise: mapHinweisMeldungen(item.hinweismeldungen),
        };
        return contract;
      });
    } else if (responseModel?.standardProdukte) {
      fullContracts = extractStandardVertraegeFromResponse(responseModel);
    }

    if (fullContracts.length > 0) {
      enrichedWeitereVertraege = weitereVertraegeCached.map(cachedItem => {
        const cachedVertragsnummer = cachedItem.asNummer.startsWith('AS-')
          ? cachedItem.asNummer
          : 'AS-' + cachedItem.asNummer;

        const fullContract = fullContracts.find(
          contract => contract.vertragsnummer === cachedVertragsnummer,
        );

        if (fullContract) {
          return {
            zielprodukt: cachedItem.zielprodukt,
            asNummer: cachedItem.asNummer,
            vnWechsel: cachedItem.vnWechsel,
            fullContract: fullContract,
          } as VertragItemWithFullContract;
        }

        return cachedItem;
      });
    }

    yield put(new DashboardActions().weitereVertreageUbernehmen(enrichedWeitereVertraege));
    yield delay(0);

    yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().vnWechselChanged({
        fremdVn: {} as FremdVN,
        removeVertraege: mapFremdVNVorvertraege(
          enrichedWeitereVertraege
            .filter(value => !value.vnWechsel)
            .map(item => mapVertragItemToVorvertrag(item, (item as VertragItemWithFullContract).fullContract)),
        ),
        addVertraege: mapFremdVNVorvertraege(
          enrichedWeitereVertraege
            .filter(value => value.vnWechsel)
            .map(item => mapVertragItemToVorvertrag(item, (item as VertragItemWithFullContract).fullContract)),
        ),
        isWeitererVertrag: true,
      }),
    );
  }
}

export function* updateVertragdaten(responseModel: VertragsuebersichtResponseModel): SagaIterator {
  if (responseModel.fremdStandaloneProdukte?.length > 0) {
    yield put(
      new DashboardActions().updateFremdeStandaloneProdukte(responseModel.fremdStandaloneProdukte),
    );
  }
  if (responseModel.fremdStandardProdukte?.length > 0) {
    yield put(new DashboardActions().updateFremdeVorvertraege(responseModel.fremdStandardProdukte));
  }
  const vorvertraege: Array<Vorvertrag> = extractStandardVertraegeFromResponse(responseModel);
  const standaloneProdukte: Array<StandaloneProdukt> =
    extractStandaloneVertraegeFromResponse(responseModel);
  const bankverbindungen: Array<BankverbindungVorvertrag> = responseModel.bankverbindungen;
  yield call(
    GlobalPut.getInstance().put,
    new GlobalActions().updateVorvertraege(
      vorvertraege,
      standaloneProdukte,
      bankverbindungen,
      responseModel.maxLimitUmstellungszwangErreicht,
    ),
  );
}

export function getBasicDaten(vertrag: Vorvertrag | StandaloneProdukt): BasicVertragsdaten {
  return {
    spLaufnummer: vertrag.spLaufnummer,
    vertragsnummer: vertrag.vertragsnummer,
    storniert: vertrag.isStorniert,
    stornoDetail: vertrag.stornoDetail,
    verkaufsangebotKurznamen: vertrag.verkaufsangebotKurznamen,
    beibehaltung: (vertrag as Vorvertrag).beibehaltung,
    ruhend: (vertrag as Vorvertrag).ruhend,
  };
}

export function getBasicDatenForWeiterenVorvertrag(
  vertrag: Vorvertrag,
): WeitereVertraegeVertragsdaten {
  return {
    vertragsnummer: vertrag.vertragsnummer,
    ersetzen: vertrag.ersetzen,
    firmenvertrag: vertrag.firmenvertrag,
    umstellungszwang: vertrag.umstellungszwang,
    sparteType: getSpartenTypeByApp(vertrag.produktIds[0]),
  };
}

function isOlderThan99(kunde: PersonenSearchListItem) {
  return isOlderThan(kunde.geburtsdatum, dayjs().subtract(1, 'day'), 99);
}

function isUnfall(appState: IAppState): boolean {
  return appState.transientState.personensucheModal == PersonensucheModal.UNFALL;
}

function isPersonBereitsTarifiert(person: PersonenSearchListItem, dashboardState: IAppState) {
  return dashboardState.warenkorb
    .map(value => value.versichertesRisiko)
    .includes(getBezeichnungForPerson(person.personData));
}

function getHasPersonVorvertraege(person: PersonenSearchListItem, appState: IAppState) {
  return hasPersonVorvertraege(
    [...appState.vorvertraege, ...appState.fremdVertraege],
    person.personData,
  );
}
